import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Actions from '../../../../actions'
import $t from '../../../../i18n'
import { Button } from 'antd'
import documentsLogo from 'resources/documentsLogo.png'
import Card from 'components/common/Card'
import Alert from 'components/common/Alert'
import { event, eventTypes } from 'consts/eventConsts'
import {
  CoverageBenefitsDocuments,
  CoverageBenefitsHMODocuments,
  routes
} from 'consts'
import { getDocLink } from 'utils/coverageBenefitsUtils'
import './DocumentsCard.css'

const DocumentsCard = ({ subscription, member }) => {
  const DOC_ICON = 'document'
  const SEARCH_ICON = 'cb-search'

  const dispatch = useDispatch()
  const authenticatedLinks = useSelector(
    state => state.profile?.authenticatedLinks
  )
  const priceTransparencyLink = member?.hmo_data?.price_transparency_link
  const planSummaryPdf = subscription?.plan_details?.plan_cb_summary_pdf
  const planCoverageLevel = subscription?.plan_details?.plan_coverage_level
  const isDentalIncluded = !!subscription?.plan_details.is_adult_dental_hmo_plan
  const userHomeAddressState = useSelector(
    state => state.account?.userHomeAddressState
  )
  const selectedEnrollmentYear = useSelector(
    state => state.payment?.selectedEnrollmentYear
  )
  const [documentsLinks, setDocumentsLinks] = useState(null)
  const [isOpynAlertVisible, setIsOpynAlertVisible] = useState(false)

  useEffect(() => {
    if (!authenticatedLinks || !userHomeAddressState) {
      dispatch(
        Actions.createAction(Actions.FRONTEND_AUTHENTICATED_URLS_REQUESTED)
      )
    } else {
      setDocumentsLinks(authenticatedLinks[userHomeAddressState])
    }
  }, [authenticatedLinks, userHomeAddressState, dispatch])

  useEffect(() => {
    if (selectedEnrollmentYear) {
      dispatch(
        Actions.createAction(Actions.FRONTEND_AUTHENTICATED_URLS_REQUESTED, {
          year: selectedEnrollmentYear
        })
      )
    }
  }, [selectedEnrollmentYear, dispatch])

  const openDocumentLink = document => {
    if (document === CoverageBenefitsDocuments.FIND_A_PROVIDER) {
      dispatch(
        Actions.createAction(Actions.NAVIGATED_TO, routes.FIND_A_PROVIDER)
      )
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.click,
          eventName: event.clickEvent.click,
          element: 'Coverage Benefits - Find a provider'
        })
      )
      return
    }
    if (document === CoverageBenefitsDocuments.SUMMARY_BENEFITS) {
      if (planSummaryPdf) window.open(planSummaryPdf)
      return
    }
    if (document === CoverageBenefitsDocuments.PRICE_TRANSPARENCY) {
      if (priceTransparencyLink) {
        // todo uncomment when we allow X-Frame-Options to include opyn domain
        // dispatch(
        //   Actions.createAction(
        //     Actions.NAVIGATED_TO,
        //     `${routes.IFRAME_VIEW}?${setUrlParams({
        //       link: encodeURIComponent(priceTransparencyLink),
        //       memberName: member.firstName,
        //     })}`
        //   )
        // )
        setIsOpynAlertVisible(true)
      }
      return
    }
    window.open(
      getDocLink({
        authenticatedLinks,
        userHomeAddressState,
        documentName: document,
        planCoverageLevel
      })
    )
  }

  const renderDocumentLink = (docName, iconType) => {
    const docLink = getDocLink({
      authenticatedLinks,
      userHomeAddressState,
      documentName: docName,
      planCoverageLevel
    })
    if (!docLink) return null

    return (
      <div
        className='documents-card-btn trackable'
        data-name={'document-' + docName}
        onClick={() => openDocumentLink(docName)}
      >
        <i className={`icon-${iconType}`} />
        <Button type='link' className='document-btn'>
          {$t(`pages.coverageBenefits.documents.${docName}`)}
        </Button>
      </div>
    )
  }

  const analyticsEvent = eventName => {
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.click,
        eventName: event.clickEvent.click,
        element: eventName
      })
    )
  }

  const navigateToOpynLink = () => {
    analyticsEvent(
      `SSO link clicked - ${CoverageBenefitsDocuments.PRICE_TRANSPARENCY}`
    )
    setIsOpynAlertVisible(false)
    window.open(priceTransparencyLink, '_blank')
  }

  return (
    <Card className='documents-card'>
      <div className='documents-card-title'>
        <img
          src={documentsLogo}
          alt='coverage-benefits'
          className='document-image'
        />
        <h2 className='coverage-benefits-card-title semi-bold'>
          {$t('pages.coverageBenefits.planDocumentsTools')}
        </h2>
      </div>
      {planSummaryPdf && (
        <div
          className='documents-card-btn trackable'
          data-name={
            'document-' + CoverageBenefitsHMODocuments.SUMMARY_BENEFITS
          }
          onClick={() =>
            openDocumentLink(CoverageBenefitsHMODocuments.SUMMARY_BENEFITS)
          }
        >
          <i className='icon-document' />
          <Button type='link' className='document-btn'>
            {$t(
              `pages.coverageBenefits.documents.${CoverageBenefitsHMODocuments.SUMMARY_BENEFITS}`
            )}
          </Button>
        </div>
      )}
      {renderDocumentLink(
        CoverageBenefitsHMODocuments.EVIDENCE_OF_COVERAGE,
        DOC_ICON
      )}
      {renderDocumentLink(
        CoverageBenefitsHMODocuments.EVIDENCE_OF_COVERAGE_AMENDMENT,
        DOC_ICON
      )}
      {renderDocumentLink(
        CoverageBenefitsHMODocuments.APPEALS_PROCESS,
        DOC_ICON
      )}
      {renderDocumentLink(
        CoverageBenefitsHMODocuments.VIRTUAL_CARE_RIDER,
        DOC_ICON
      )}
      {isDentalIncluded &&
        renderDocumentLink(CoverageBenefitsHMODocuments.DENTAL_RIDER, DOC_ICON)}
      {renderDocumentLink(CoverageBenefitsHMODocuments.PLAN_BROCHURE, DOC_ICON)}
      {renderDocumentLink(
        CoverageBenefitsHMODocuments.PRIOR_AUTH_GUIDELINES,
        DOC_ICON
      )}
      {renderDocumentLink(CoverageBenefitsHMODocuments.DRUG_LIST, DOC_ICON)}
      {renderDocumentLink(
        CoverageBenefitsHMODocuments.MEDICATION_SEARCH,
        SEARCH_ICON
      )}
      {renderDocumentLink(
        CoverageBenefitsHMODocuments.PHARMACY_SEARCH,
        SEARCH_ICON
      )}
      <div
        className='documents-card-btn trackable'
        data-name={'document-' + CoverageBenefitsHMODocuments.FIND_A_PROVIDER}
        onClick={() =>
          openDocumentLink(CoverageBenefitsHMODocuments.FIND_A_PROVIDER)
        }
      >
        <i className='icon-cb-search' />
        <Button type='link' className='document-btn'>
          {$t(
            `pages.coverageBenefits.documents.${CoverageBenefitsHMODocuments.FIND_A_PROVIDER}`
          )}
        </Button>
      </div>
      {priceTransparencyLink && (
        <div
          className='documents-card-btn trackable'
          data-name={'document-' + CoverageBenefitsDocuments.PRICE_TRANSPARENCY}
          onClick={() =>
            openDocumentLink(CoverageBenefitsDocuments.PRICE_TRANSPARENCY)
          }
        >
          <i className='icon-cb-search' />
          <Button type='link' className='document-btn'>
            {$t(
              `pages.coverageBenefits.documents.${CoverageBenefitsDocuments.PRICE_TRANSPARENCY}`
            )}
          </Button>
        </div>
      )}
      <Alert
        title={$t(`pages.coverageBenefits.redirectTitle`)}
        className='redirect-note'
        shouldDisplay={isOpynAlertVisible}
        onCancel={() => setIsOpynAlertVisible(false)}
        onOk={navigateToOpynLink}
        closable={true}
        OkText={$t(`common.continue`)}
      >
        {$t(`pages.coverageBenefits.documents.redirectOpynMsg`)}
      </Alert>
    </Card>
  )
}

export default DocumentsCard
